import { Controller } from "@hotwired/stimulus"
import { showPageFlash } from "../../../src/application/flash_messages.js"

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

/**
 * Reflects railsui-date-picker selection onto the to-do date rail/bar.
 * Optional submitUrl is unused when blank (library/demo); product can set it later
 * or listen for the `todo-date:changed` event.
 */
export default class extends Controller {
  static targets = ["input", "month", "day", "emptyIcon", "datedText", "trigger"]
  static values = {
    type: { type: String, default: "self_assigned" },
    submitUrl: { type: String, default: "" },
    submitMethod: { type: String, default: "POST" }
  }

  connect() {
    this.lastCommittedValue = this.hasInputTarget ? this.inputTarget.value : ""
    if (this.hasInputTarget && this.inputTarget.value) {
      this.reflect(this.inputTarget.value)
    }
  }

  dateChanged() {
    if (!this.hasInputTarget) return

    const rollback = this.snapshotDateUi()
    const value = this.inputTarget.value
    this.reflect(value)
    this.dispatch("changed", {
      detail: {
        value,
        type: this.typeValue,
        name: this.inputTarget.name
      }
    })
    this.persistSelection(value, rollback)
    if (!this.submitUrlValue) this.lastCommittedValue = value
  }

  reflect(rawValue) {
    const parsed = this.parseDate(rawValue)
    if (!parsed) {
      if (this.typeValue === "none") this.element.removeAttribute("data-dated")
      return
    }

    const month = MONTHS[parsed.getMonth()]
    const day = String(parsed.getDate())

    this.monthTargets.forEach((el) => {
      el.textContent = month
    })
    this.dayTargets.forEach((el) => {
      el.textContent = day
    })

    this.emptyIconTargets.forEach((el) => {
      el.classList.add("hidden")
      el.hidden = true
    })

    this.datedTextTargets.forEach((el) => {
      el.classList.remove("hidden")
      el.classList.add("inline-flex")
      el.hidden = false
    })

    if (this.typeValue === "none") {
      this.markDated()
    }

    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-label", `${month} ${day}. Choose date`)
    }
  }

  markDated() {
    this.element.setAttribute("data-dated", "true")
  }

  persistSelection(value, rollback) {
    if (!this.submitUrlValue) return

    window.fetch(this.submitUrlValue, {
      method: (this.submitMethodValue || "POST").toUpperCase(),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": this.csrfToken
      },
      body: JSON.stringify({
        [this.hasInputTarget ? this.inputTarget.name : "todo_date"]: value
      })
    })
      .then((response) => this.handlePersistResponse(response, rollback))
      .catch(() => {
        this.rollbackDateUi(rollback)
        showPageFlash("Unable to update the due date.")
      })
  }

  async handlePersistResponse(response, rollback) {
    if (response.ok) {
      this.lastCommittedValue = this.hasInputTarget ? this.inputTarget.value : this.lastCommittedValue
      return true
    }

    let message = "Unable to update the due date."
    try {
      const body = await response.json()
      message = body.error || body.goal_date?.join(", ") || message
    } catch (_error) {
      // Keep the fallback message when the response is not JSON.
    }
    this.rollbackDateUi(rollback)
    showPageFlash(message)
    return false
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  parseDate(value) {
    if (!value) return null
    const match = String(value).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
    if (match) {
      const month = Number(match[1]) - 1
      const day = Number(match[2])
      const year = Number(match[3])
      return new Date(year, month, day)
    }
    const asDate = new Date(value)
    return Number.isNaN(asDate.getTime()) ? null : asDate
  }

  snapshotDateUi() {
    return {
      inputValue: this.hasInputTarget ? this.inputTarget.value : "",
      committedValue: this.lastCommittedValue,
      monthText: this.monthTargets.map((el) => el.textContent),
      dayText: this.dayTargets.map((el) => el.textContent),
      emptyHidden: this.emptyIconTargets.map((el) => el.hidden),
      datedHidden: this.datedTextTargets.map((el) => el.hidden),
      rootClasses: this.typeValue === "none" ? Array.from(this.element.classList) : null,
      triggerLabel: this.hasTriggerTarget ? this.triggerTarget.getAttribute("aria-label") : null
    }
  }

  rollbackDateUi(snapshot) {
    if (!snapshot) return

    if (this.hasInputTarget) this.inputTarget.value = snapshot.committedValue || ""
    this.monthTargets.forEach((el, idx) => {
      el.textContent = snapshot.monthText?.[idx] || ""
    })
    this.dayTargets.forEach((el, idx) => {
      el.textContent = snapshot.dayText?.[idx] || ""
    })
    this.emptyIconTargets.forEach((el, idx) => {
      const hidden = snapshot.emptyHidden?.[idx]
      el.hidden = !!hidden
      el.classList.toggle("hidden", !!hidden)
    })
    this.datedTextTargets.forEach((el, idx) => {
      const hidden = snapshot.datedHidden?.[idx]
      el.hidden = !!hidden
      el.classList.toggle("hidden", !!hidden)
      el.classList.toggle("inline-flex", !hidden)
    })
    if (snapshot.rootClasses) this.element.className = snapshot.rootClasses.join(" ")
    if (this.hasTriggerTarget && snapshot.triggerLabel !== null) {
      this.triggerTarget.setAttribute("aria-label", snapshot.triggerLabel)
    }
  }
}
